import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ArrowLeft, CheckCircle2, XCircle, Star, StarOff, Archive,
  Loader2, AlertCircle, ImageIcon, Edit3, Save, X as XIcon,
  Package, Tag, MapPin, Box,
} from 'lucide-react';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { adminApi, http } from '@/services/api';
import { formatNPR, formatDate } from '@/utils/format';
import { FileImageUploader, FileImageGallery } from '@/components/shared/FileImageUploader';
import {
  VariantEditor, variantsToPayload, variantStateFromProduct, type VariantState,
} from '@/components/shared/VariantEditor';
import { Badge, Button, Card, CardContent, Input, RichTextEditor } from '@nepal-ecom/ui';

const STATUS_OPTIONS: { value: ProductStatus; label: string }[] = [
  { value: 'draft', label: 'Draft (pending review)' },
  { value: 'active', label: 'Active (published)' },
  { value: 'out_of_stock', label: 'Out of stock' },
  { value: 'archived', label: 'Archived' },
];

type ProductStatus = 'draft' | 'active' | 'out_of_stock' | 'archived';

interface Product {
  id: string;
  name: string;
  nameNp: string | null;
  slug: string;
  description: string | null;
  sku: string | null;
  price: number;
  compareAtPrice: number | null;
  discountPercent: number | null;
  stock: number;
  isMadeInNepal: boolean;
  isFeatured: boolean;
  status: ProductStatus;
  images: string[] | null;
  thumbnailUrl: string | null;
  weight: number | null;
  dimensions: { length: number; width: number; height: number; unit: string } | null;
  tags: string[] | null;
  rating: number;
  totalReviews: number;
  totalSold: number;
  metaTitle: string | null;
  metaDescription: string | null;
  hasVariants: boolean;
  variants?: any[];
  vendorId: string;
  categoryId: string;
  vendor?: { businessName: string; slug: string };
  category?: { name: string };
  createdAt: string;
}

const STATUS_LABEL: Record<ProductStatus, string> = {
  draft: 'Pending review',
  active: 'Active',
  out_of_stock: 'Out of stock',
  archived: 'Archived',
};

const STATUS_VARIANT: Record<ProductStatus, 'warning' | 'success' | 'destructive' | 'secondary'> = {
  draft: 'warning',
  active: 'success',
  out_of_stock: 'destructive',
  archived: 'secondary',
};

export function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<Product>>({});
  const [variants, setVariants] = useState<VariantState>({ attributes: [], variants: [] });
  // Only re-sync variants when the admin actually edited them — otherwise a
  // name-only save would needlessly delete+recreate every variant row.
  const [variantsTouched, setVariantsTouched] = useState(false);

  const { data: product, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin', 'product', id],
    queryFn: async () => {
      const res = await adminApi.getProduct(id!);
      return res.data.data as Product;
    },
    enabled: !!id,
  });

  useEffect(() => {
    if (product) setForm({});
  }, [product?.id]);

  const approve = useMutation({
    mutationFn: () => adminApi.approveProduct(id!),
    onSuccess: () => {
      toast.success('Product approved & published');
      qc.invalidateQueries({ queryKey: ['admin', 'product', id] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Approve failed'),
  });

  const reject = useMutation({
    mutationFn: () => adminApi.rejectProduct(id!),
    onSuccess: () => {
      toast.success('Product rejected & archived');
      qc.invalidateQueries({ queryKey: ['admin', 'product', id] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Reject failed'),
  });

  const feature = useMutation({
    mutationFn: (isFeatured: boolean) => adminApi.featureProduct(id!, isFeatured),
    onSuccess: () => {
      toast.success('Product updated');
      qc.invalidateQueries({ queryKey: ['admin', 'product', id] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Update failed'),
  });

  const update = useMutation({
    mutationFn: () => {
      // Always re-send the variant set the editor currently shows. The editor
      // is seeded from the existing product on edit, so an untouched product
      // round-trips unchanged; an emptied editor clears variants. When variants
      // exist, the product's base stock mirrors the sum of variant stock.
      const payload: Record<string, unknown> = { ...form };
      // Only touch variants if the admin edited them in this session. Sending
      // the key triggers a full re-sync (delete + recreate) server-side.
      if (variantsTouched) {
        const variantPayload = variantsToPayload(variants);
        if (variantPayload) {
          payload.variants = variantPayload;
          payload.stock = variantPayload.reduce((sum, v) => sum + (Number(v.stock) || 0), 0);
        } else {
          payload.variants = [];
        }
      }
      return http.put(`/products/${id}`, payload);
    },
    onSuccess: () => {
      toast.success('Changes saved');
      setEditing(false);
      setForm({});
      qc.invalidateQueries({ queryKey: ['admin', 'product', id] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Save failed'),
  });

  const startEditing = () => {
    setForm({});
    setVariants(variantStateFromProduct(product?.variants));
    setVariantsTouched(false);
    setEditing(true);
  };

  if (isLoading) {
    return (
      <div className="animate-fade-in flex items-center justify-center py-32 text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading product…
      </div>
    );
  }
  if (isError || !product) {
    return (
      <div className="animate-fade-in py-20 text-center">
        <AlertCircle className="h-10 w-10 mx-auto text-red-500 mb-3" />
        <p className="text-sm text-slate-600 mb-4">Could not load product.</p>
        <Button size="sm" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  const displayValue = <K extends keyof Product>(key: K): Product[K] =>
    (form[key] !== undefined ? form[key] : product[key]) as Product[K];

  const setField = <K extends keyof Product>(key: K, value: Product[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  return (
    <div className="animate-fade-in">
      <Breadcrumb items={[{ label: 'Catalog', to: '/products' }, { label: product.name }]} />

      <button
        onClick={() => navigate('/products')}
        className="text-xs text-slate-500 hover:text-primary-600 flex items-center gap-1 mb-3"
      >
        <ArrowLeft className="h-3 w-3" /> Back to catalog
      </button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-900">{product.name}</h1>
            <Badge variant={STATUS_VARIANT[product.status]}>{STATUS_LABEL[product.status]}</Badge>
            {product.isFeatured && (
              <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                <Star className="h-3 w-3 fill-amber-500" /> Featured
              </span>
            )}
            {product.isMadeInNepal && (
              <span className="text-xs bg-rose-100 text-rose-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                <MapPin className="h-3 w-3" /> Made in Nepal
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500">
            {product.vendor && (
              <Link to={`/vendors/${product.vendorId}`} className="text-primary-600 hover:underline">
                {product.vendor.businessName}
              </Link>
            )}
            {product.category && <span> · {product.category.name}</span>}
            {product.sku && <span className="ml-2 font-mono text-xs">SKU: {product.sku}</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {product.status === 'draft' && (
            <>
              <Button size="sm" onClick={() => approve.mutate()} disabled={approve.isPending}>
                {approve.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <CheckCircle2 className="h-4 w-4 mr-1" />}
                Approve
              </Button>
              <Button size="sm" variant="outline" onClick={() => reject.mutate()} disabled={reject.isPending} className="text-red-600 border-red-200 hover:bg-red-50">
                <XCircle className="h-4 w-4 mr-1" /> Reject
              </Button>
            </>
          )}
          {product.status === 'active' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => feature.mutate(!product.isFeatured)}
              disabled={feature.isPending}
            >
              {product.isFeatured ? <StarOff className="h-4 w-4 mr-1" /> : <Star className="h-4 w-4 mr-1" />}
              {product.isFeatured ? 'Unfeature' : 'Feature'}
            </Button>
          )}
          {!editing && (
            <Button size="sm" variant="outline" onClick={startEditing}>
              <Edit3 className="h-4 w-4 mr-1" />
              {product.status === 'archived' ? 'Edit archived' : 'Edit'}
            </Button>
          )}
          {editing && (
            <>
              <Button size="sm" onClick={() => update.mutate()} disabled={update.isPending}>
                {update.isPending ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
                Save changes
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setForm({}); setVariants({ attributes: [], variants: [] }); setVariantsTouched(false); }}>
                <XIcon className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Images */}
          <Card>
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                <ImageIcon className="h-4 w-4" /> Media
              </h2>
              {editing ? (
                <div className="space-y-4">
                  <FileImageUploader
                    label="Thumbnail"
                    value={(displayValue('thumbnailUrl') as string) ?? ''}
                    onChange={(url) => setField('thumbnailUrl', url)}
                    aspect="aspect-square"
                    helperText="Square card image used in lists and search."
                  />
                  <FileImageGallery
                    label="Gallery"
                    values={(displayValue('images') as string[]) ?? []}
                    onChange={(urls) => setField('images', urls)}
                    max={10}
                    helperText="Drag-and-drop to upload. First image is the primary."
                  />
                </div>
              ) : product.images?.length || product.thumbnailUrl ? (
                <div className="grid grid-cols-4 gap-2">
                  {(product.images?.length ? product.images : product.thumbnailUrl ? [product.thumbnailUrl] : []).map((src, i) => (
                    <img key={i} src={src} className="w-full aspect-square object-cover rounded-lg bg-slate-100" />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">No images uploaded</p>
              )}
            </CardContent>
          </Card>

          {/* Description */}
          <Card>
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-slate-800 mb-3">Description</h2>
              {editing ? (
                <RichTextEditor
                  value={(displayValue('description') as string) ?? ''}
                  onChange={(html) => setField('description', html)}
                  placeholder="Describe the product…"
                />
              ) : product.description ? (
                <div
                  className="prose prose-sm max-w-none text-slate-700"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              ) : (
                <span className="italic text-slate-400 text-sm">No description</span>
              )}
            </CardContent>
          </Card>

          {/* Variants */}
          {editing ? (
            <Card>
              <CardContent className="p-5">
                <h2 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Box className="h-4 w-4" /> Variants
                </h2>
                <p className="text-xs text-slate-500 mb-4">
                  Add, edit, or remove variant combinations (size, color, etc.). Each row has its own
                  SKU, price, and stock. Leave empty for a single-SKU product — the base price/stock
                  above will be used.
                </p>
                <VariantEditor
                  basePrice={Number(displayValue('price')) || Number(product.price) || 0}
                  baseStock={Number(displayValue('stock')) || Number(product.stock) || 0}
                  value={variants}
                  onChange={(v) => { setVariants(v); setVariantsTouched(true); }}
                />
              </CardContent>
            </Card>
          ) : product.variants && product.variants.length > 0 ? (
            <Card>
              <CardContent className="p-5">
                <h2 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Box className="h-4 w-4" /> Variants ({product.variants.length})
                </h2>
                <table className="w-full text-sm">
                  <thead className="text-xs text-slate-500 uppercase">
                    <tr>
                      <th className="text-left py-2">Image</th>
                      <th className="text-left">Name</th>
                      <th className="text-left">Options</th>
                      <th className="text-left">SKU</th>
                      <th className="text-right">Price</th>
                      <th className="text-right">Stock</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {product.variants.map((v: any) => {
                      const optionsText = v.options && typeof v.options === 'object'
                        ? Object.entries(v.options as Record<string, string>)
                            .map(([k, val]) => `${k}: ${val}`)
                            .join(', ')
                        : '';
                      return (
                        <tr key={v.id}>
                          <td className="py-2">
                            {v.imageUrl ? (
                              <img src={v.imageUrl} alt="" className="h-9 w-9 rounded-md object-cover ring-1 ring-slate-200" />
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                          <td className="py-2">{v.name}</td>
                          <td className="text-slate-600">{optionsText || '—'}</td>
                          <td className="font-mono text-xs">{v.sku ?? '—'}</td>
                          <td className="text-right tabular-nums">{formatNPR(Number(v.price))}</td>
                          <td className="text-right tabular-nums">{v.stock}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          ) : null}

          {/* SEO */}
          <Card>
            <CardContent className="p-5">
              <h2 className="text-sm font-semibold text-slate-800 mb-3">SEO</h2>
              <div className="space-y-3 text-sm">
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1">Meta title</p>
                  {editing ? (
                    <Input value={(displayValue('metaTitle') as string) ?? ''} onChange={(e) => setField('metaTitle', e.target.value)} />
                  ) : (
                    <p className="text-slate-700">{product.metaTitle ?? <span className="italic text-slate-400">Not set</span>}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1">Meta description</p>
                  {editing ? (
                    <textarea value={(displayValue('metaDescription') as string) ?? ''} onChange={(e) => setField('metaDescription', e.target.value)} rows={2} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
                  ) : (
                    <p className="text-slate-700">{product.metaDescription ?? <span className="italic text-slate-400">Not set</span>}</p>
                  )}
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 mb-1">Slug</p>
                  <p className="text-slate-700 font-mono text-xs">/product/{product.slug}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-5">
          <Card><CardContent className="p-5">
            <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">Pricing</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-500 mb-1">Price</p>
                {editing ? (
                  <Input type="number" min="0" step="0.01" value={(displayValue('price') as number) ?? 0} onChange={(e) => setField('price', Number(e.target.value))} />
                ) : (
                  <p className="text-2xl font-bold text-slate-900 tabular-nums">{formatNPR(Number(product.price))}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Compare at <span className="text-slate-400">(strike-through)</span></p>
                {editing ? (
                  <Input
                    type="number" min="0" step="0.01"
                    placeholder="Original price"
                    value={(displayValue('compareAtPrice') as number | null) ?? ''}
                    onChange={(e) => setField('compareAtPrice', e.target.value === '' ? null : Number(e.target.value))}
                  />
                ) : product.compareAtPrice ? (
                  <p className="text-sm text-slate-500 line-through tabular-nums">{formatNPR(Number(product.compareAtPrice))}</p>
                ) : (
                  <span className="italic text-slate-400 text-sm">Not set</span>
                )}
              </div>
              {!editing && product.discountPercent && (
                <Badge variant="success">{product.discountPercent}% off</Badge>
              )}
              {editing && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Status</p>
                  <select
                    value={(displayValue('status') as ProductStatus) ?? 'draft'}
                    onChange={(e) => setField('status', e.target.value as ProductStatus)}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-400 mt-1">Set to “Active” to republish a draft or archived product.</p>
                </div>
              )}
            </div>
          </CardContent></Card>

          <Card><CardContent className="p-5">
            <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3 flex items-center gap-2"><Package className="h-3.5 w-3.5" /> Inventory</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">In stock</span>
                {editing ? (
                  <Input type="number" value={(displayValue('stock') as number) ?? 0} onChange={(e) => setField('stock', Number(e.target.value))} className="w-24 h-7" />
                ) : (
                  <span className="font-semibold tabular-nums">{product.stock}</span>
                )}
              </div>
              <div className="flex justify-between"><span className="text-slate-600">Sold</span><span className="font-semibold tabular-nums">{product.totalSold}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Rating</span><span className="font-semibold">{product.rating > 0 ? `★ ${product.rating}` : '—'} <span className="text-xs text-slate-400">({product.totalReviews})</span></span></div>
            </div>
          </CardContent></Card>

          {(editing || (product.tags && product.tags.length > 0)) && (
            <Card><CardContent className="p-5">
              <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3 flex items-center gap-2"><Tag className="h-3.5 w-3.5" /> Tags</h3>
              {editing ? (
                <TagEditor
                  value={(displayValue('tags') as string[]) ?? []}
                  onChange={(next) => setField('tags', next)}
                />
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {product.tags!.map((t) => (
                    <span key={t} className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              )}
            </CardContent></Card>
          )}

          <Card><CardContent className="p-5">
            <h3 className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-3">Shipping</h3>
            <div className="space-y-1 text-sm text-slate-600">
              <div className="flex justify-between"><span>Weight</span><span className="tabular-nums">{product.weight ? `${product.weight} kg` : '—'}</span></div>
              <div className="flex justify-between"><span>Dimensions</span>
                <span className="tabular-nums text-xs">
                  {product.dimensions
                    ? `${product.dimensions.length}×${product.dimensions.width}×${product.dimensions.height} ${product.dimensions.unit}`
                    : '—'}
                </span>
              </div>
            </div>
          </CardContent></Card>

          <p className="text-xs text-slate-400 text-center">Added {formatDate(product.createdAt)}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Inline tag editor used in edit mode ─────────────────────────────────────
function TagEditor({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (!t || value.includes(t)) return;
    onChange([...value, t]);
    setDraft('');
  };
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder="Add a tag and press Enter"
          className="h-8 text-sm"
        />
        <Button type="button" variant="outline" size="sm" onClick={add} disabled={!draft.trim()}>
          Add
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((t) => (
            <span key={t} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full">
              {t}
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x !== t))}
                className="text-slate-400 hover:text-red-500"
                aria-label={`Remove tag ${t}`}
              >
                <XIcon className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

